import { fetchAuthSession } from 'aws-amplify/auth';

export interface WebSocketMessage {
  type: string;
  server_name?: string;
  event_type?: string;
  timestamp?: string | number;
  message?: string;
  server_data?: {
    status?: string;
    public_ip?: string;
    current_instance_id?: string;
    updated_at?: string;
    cost_tracking?: {
      total_runtime_hours?: number;
      total_cost_usd?: number;
      cost_per_hour?: number;
    };
    deleted?: boolean;
  };
}

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private isConnecting = false;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private heartbeatInterval = 30000; // 30 seconds
  private wsUrl: string;

  constructor(wsUrl: string) {
    this.wsUrl = wsUrl;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.isConnected) {
      console.log('🔌 WebSocket already connecting or connected');
      return;
    }

    try {
      this.isConnecting = true;
      console.log('🔌 Establishing WebSocket connection...');

      // Get the auth token (access token for Cognito get_user API)
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        throw new Error('No authentication token available');
      }

      // Construct WebSocket URL with token
      const wsUrlWithToken = `${this.wsUrl}?token=${encodeURIComponent(accessToken)}`;
      console.log('🔌 Connecting to WebSocket with token...');

      this.ws = new WebSocket(wsUrlWithToken);

      this.ws.onopen = (event) => {
        console.log('✅ WebSocket connected successfully');
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000; // Reset delay
        this.startHeartbeat();
        this.emit('connection', { type: 'connection', message: 'Connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('📨 Received WebSocket message:', message);
          
          // Emit based on message type
          this.emit(message.type, message);
          this.emit('message', message);
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket connection closed:', event.code, event.reason);
        this.isConnecting = false;
        this.isConnected = false;
        this.stopHeartbeat();
        this.emit('disconnect', { type: 'disconnect', message: 'Disconnected' });

        // Auto-reconnect if not a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', { type: 'error', message: 'Connection error' });
      };

    } catch (error) {
      console.error('❌ Failed to establish WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('error', { type: 'error', message: 'Failed to connect' });
      throw error;
    }
  }

  disconnect(): void {
    console.log('🔌 Disconnecting WebSocket...');
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      this.connect().catch(error => {
        console.error('❌ Reconnect failed:', error);
      });
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    // Note: API Gateway WebSocket has built-in connection management
    // Heartbeat/ping messages not needed and would cause "Forbidden" errors
    // since there's no route configured to handle them
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  on(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  off(eventType: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(eventType: string, message: WebSocketMessage): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error(`❌ Error in WebSocket event handler for ${eventType}:`, error);
        }
      });
    }
  }

  getConnectionStatus(): { connected: boolean; connecting: boolean; reconnectAttempts: number } {
    return {
      connected: this.isConnected,
      connecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Force reconnect (useful for manual retry)
  reconnect(): void {
    console.log('🔄 Manual reconnect requested');
    this.disconnect();
    setTimeout(() => {
      this.connect().catch(error => {
        console.error('❌ Manual reconnect failed:', error);
      });
    }, 1000);
  }
}

// Singleton instance
let webSocketService: WebSocketService | null = null;

export const getWebSocketService = (wsUrl?: string): WebSocketService => {
  if (!webSocketService && wsUrl) {
    webSocketService = new WebSocketService(wsUrl);
  }
  if (!webSocketService) {
    throw new Error('WebSocket service not initialized. Call getWebSocketService with wsUrl first.');
  }
  return webSocketService;
};

export default WebSocketService;